/**
 * Reset Password Page - Cambiar contraseña con oobCode
 * Se abre desde /auth/action?mode=resetPassword&oobCode=...
 */
import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { Button, Input, InlineLoader } from "@/components/ui";
import { auth } from "@/lib/firebase";
import { verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = searchParams.get("oobCode");

  const [verifying, setVerifying] = useState(true);
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Verificar el código al montar
  useEffect(() => {
    if (!oobCode) {
      setError("El enlace es inválido o está incompleto.");
      setVerifying(false);
      return;
    }
    verifyPasswordResetCode(auth, oobCode)
      .then((em) => {
        setEmail(em);
        setVerifying(false);
      })
      .catch((err) => {
        const code = err?.code || "";
        if (code === "auth/expired-action-code") {
          setError(
            "Este enlace ha expirado. Solicita uno nuevo desde la pantalla de inicio de sesión.",
          );
        } else if (code === "auth/invalid-action-code") {
          setError(
            "Este enlace ya fue usado o no es válido. Solicita uno nuevo.",
          );
        } else {
          setError("No pudimos validar el enlace. Inténtalo de nuevo.");
        }
        setVerifying(false);
      });
  }, [oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oobCode) return;
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      // El enlace puede caducar ENTRE que se abre la pantalla y se pulsa el
      // botón: pedir otro correo de recuperación invalida el anterior aunque
      // ya lo tengas abierto. Con el mensaje genérico de antes, el usuario
      // reintentaba con otra contraseña una y otra vez sin saber que el
      // problema era el enlace (le pasó a una directora el 2026-08-06).
      const e = err as { code?: string };
      if (e.code === "auth/weak-password") {
        setError("La contraseña es demasiado débil. Usa al menos 6 caracteres.");
      } else if (e.code === "auth/expired-action-code") {
        setError(
          "Este enlace ya expiró. Vuelve a 'Olvidaste tu contraseña' y usa el correo más reciente.",
        );
      } else if (e.code === "auth/invalid-action-code") {
        setError(
          "Este enlace ya no sirve: se usó antes, o pediste otro correo después y ese es el que vale. Solicita uno nuevo y ábrelo sin pedir más.",
        );
      } else if (e.code === "auth/user-disabled") {
        setError(
          "Esta cuenta está deshabilitada. Escríbele a la Asociación para reactivarla.",
        );
      } else if (e.code === "auth/user-not-found") {
        setError("La cuenta de este enlace ya no existe.");
      } else {
        setError(
          `No pudimos cambiar la contraseña. Solicita un enlace nuevo e inténtalo otra vez.${
            e.code ? ` (${e.code})` : ""
          }`,
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Verificando código
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-fuchsia-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-10 text-center max-w-md w-full">
          <InlineLoader size="lg" label="Validando enlace…" />
        </div>
      </div>
    );
  }

  // Error
  if (error && !email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-fuchsia-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-center text-gray-900 mb-2">
            Enlace no válido
          </h1>
          <p className="text-gray-600 text-center mb-6">{error}</p>
          <div className="flex flex-col gap-2">
            <Link to="/forgot-password">
              <Button variant="primary" className="w-full">
                Solicitar nuevo enlace
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="secondary" className="w-full">
                Volver al inicio
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Éxito
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-fuchsia-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            ¡Contraseña actualizada!
          </h1>
          <p className="text-gray-600 mb-6">
            Ya puedes iniciar sesión con tu nueva contraseña.
          </p>
          <Link to="/login">
            <Button variant="primary" className="w-full">
              Ir al login <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Formulario
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-fuchsia-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 mb-3 shadow-lg shadow-indigo-500/30">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Nueva contraseña</h1>
          {email && (
            <p className="text-sm text-gray-600 mt-1">
              Para <span className="font-medium">{email}</span>
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Input
              type={showPwd ? "text" : "password"}
              label="Nueva contraseña"
              placeholder="Mínimo 6 caracteres"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPwd(!showPwd)}
              className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
              aria-label="Mostrar/ocultar"
            >
              {showPwd ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>

          <Input
            type={showPwd ? "text" : "password"}
            label="Confirmar contraseña"
            placeholder="Repite la contraseña"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />

          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            loading={submitting}
            disabled={submitting}
          >
            Cambiar contraseña
          </Button>
        </form>
      </div>
    </div>
  );
}
